import React from "react";
import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SortingRule } from "react-table";
import { Col, Row } from "reactstrap";
import { Parameter } from "../../models/Parameter";
import InputComponent from "../../components/InputComponent";
import ButtonComponent from "../../components/ButtonComponent";
import TableComponent, { OperationsCell } from "../../components/TableComponent";
import Toast from "../../helpers/Toast";
import { Utilities } from "../../helpers/Utilities";
import { ProjectDTO } from "../../models/Project/ProjectDTO";
import { ProjectService } from "../../services/ProjectService";
import Routes from "../../helpers/Routes";
import Layout from "../../components/Layout/Layout";

export default function ProjectIndex() {
    const intl = useIntl();
    //Declaração de outros estados e variáveis importantes
    const navigate = useNavigate();
    const projectService = new ProjectService();
    const pageSizes: number[] = [5, 15, 30];
    const [data, setData] = useState<ProjectDTO[]>([]);
    const [loading, setLoading] = useState(false);
    const [pageCount, setPageCount] = useState(1);
    const [searchParams] = useSearchParams();

    //Vamos tentar obter a page e a pageSize pelos query parameters
    //Usamos useState em vez de simple var, para não estar sempre a obter o valor a cada re-render, desta forma o page e o pageSize são sempre o mesmo
    //porque nunca estamos a fazer setState à var
    const [page] = useState<number>(Utilities.LoadPageFromURLQuery("page"));
    const [pageSize] = useState<number>(
        Utilities.LoadPageSizeFromURLQuery(pageSizes)
    );

    //Search Parameters
    const [projectName, setProjectName] = useState<string>(
        Utilities.LoadParameterFromURLQuery("ProjectName", "string", "")
    );
    const [codClient, setCodClient] = useState<string>(
        Utilities.LoadParameterFromURLQuery("CodClient", "string", "")
    );
    const [clientName, setClientName] = useState<string>(
        Utilities.LoadParameterFromURLQuery("ClientName", "string", "")
    );
    const [startDate, setStartDate] = useState<string>(
        Utilities.LoadParameterFromURLQuery("StartDate", "string", "")
    );
    const [budget, setBudget] = useState<string>(
        Utilities.LoadParameterFromURLQuery("Budget", "string", "")
    );
    const [notes, setNotes] = useState<string>(
        Utilities.LoadParameterFromURLQuery("Notes", "string", "")
    );

    const [currentPage, setCurrentPage] = useState<number>();
    const [currentPageSize, setCurrentPageSize] = useState<number>();
    const [currentSorting, setCurrentSorting] = useState<
        SortingRule<object>[] | null
    >(null);
    const [goToPage, setGoToPage] = useState<number>();
    const [initialSortBy] = useState<SortingRule<object>[]>(
        Utilities.LoadSortParametersFromURLQuery("sortField", "sortOrder")
    );
    const [reloadSearch, setReloadSearch] = useState<boolean>(false);

    const columns = React.useMemo(
        () => [
            {
                Header: "",
                accessor: " ",
                Cell: (props: any) =>
                    OperationsCell(props.cell.row.original.erpId, "project"),
                width: 5,
            },
            {
                Header: <FormattedMessage id="Screens.Projects.Table.Column1" />,
                accessor: "description",
                width: 25,
            },
            {
                Header: <FormattedMessage id="Screens.Projects.Table.Column2" />,
                accessor: "client.name",
                width: 25,
            },
            {
                Header: <FormattedMessage id="Screens.Projects.Table.Column3" />,
                accessor: "client.erpId",
                width: 10,
            },
            {
                Header: <FormattedMessage id="Screens.Projects.Table.Column4" />,
                accessor: 'startDate',
                width: 10,
                //obter Date e não DateTime + formatar campo
                Cell: (props: any) => {
                    return <span>{Utilities.formatDate(new Date(props.cell.value))}</span>;
                },
            },
            {
                Header: <FormattedMessage id="Screens.Projects.Table.Column5" />,
                accessor: "budgetSellingPrice",
                width: 5,
                Cell: (props: any) => {
                  const { budgetSellingPrice } = props.cell.row.original;
                  const formattedBudgetSellingPrice = budgetSellingPrice ? intl.formatNumber(budgetSellingPrice, { style: 'currency', currency: 'EUR' }) : '-';
             
                  return <div className="table_align-right">{formattedBudgetSellingPrice}</div>;
                },
            },
            {
                Header: <FormattedMessage id="Screens.Projects.Table.Column6" />,
                accessor: "notes",
                width: 20,
            },
        ],
        []
    );

    const fetchData = async (
        page: number,
        pageSize: number,
        sortBy: SortingRule<object>[] | null
    ) => {
        //Atualizamos na history do browser a current page e pageSize

        var searchUrl: string = `?page=${page}&pageSize=${pageSize}` +
            `${Utilities.AddParamToQueryIfValue("description", "string", projectName)}` +
            `${Utilities.AddParamToQueryIfValue("codClient", "string", codClient)}` +
            `${Utilities.AddParamToQueryIfValue("clientName", "string", clientName)}` +
            `${Utilities.AddParamToQueryIfValue("startDate", "string", startDate ? startDate.toString() : null)}` +
            `${Utilities.AddParamToQueryIfValue("budget", "string", budget != null ? budget.toString() : null)}` +
            `${Utilities.AddParamToQueryIfValue("notes", "string", notes)}`;

        if (sortBy != null && sortBy.length > 0) {
            searchUrl += `${Utilities.AddParamToQueryIfValue(
                "sortField",
                "string",
                sortBy[0].id
            )}${Utilities.AddParamToQueryIfValue(
                "sortOrder",
                "string",
                sortBy[0].desc === true ? "DESC" : "ASC"
            )}`;
        }

        //Se antes não existirem parâmetros de pesquisa, significa que o user veio para aqui de uma rota, então para não ficar com duas rotas duplicadas, fazemos replace à rota

        if (searchParams.toString() === "") {
            navigate(`/projects${searchUrl}`, { replace: true });
        } else {
            //Se já existirem parâmetros de pesquisa, não fazemos replace mas push
            navigate(`/projects${searchUrl}`);
        }

        setLoading(true);

        //Vamos obter os dados
        var response = await projectService.GetAllFromErp(
            mountSearchParameters(),
            Utilities.MountSortParameters(sortBy),
            page,
            pageSize
        );

        if (response.success !== true) {
            setData([]);
            setPageCount(0);
            setLoading(false);
            setCurrentPageSize(0);
            setCurrentPage(0);
            setCurrentSorting(null);
            Toast.Show("error", response.message);
            return;
        }

        //Atribuímos os dados
        setData(response.items);
        setPageCount(response.totalPages);
        setLoading(false);
        setCurrentPageSize(pageSize);
        setCurrentPage(page);
        setCurrentSorting(sortBy);
        setGoToPage(0);

        //Se a pesquisa tiver retornado múltiplas páginas e a página atual for superior ao nº de páginas que chegaram mandamos o user para a página 1
        if (response.totalPages > 0 && page > response.totalPages) {
            setGoToPage(1);
        }
    };

    //Método para montar os parâmetros de pesquisa
    const mountSearchParameters = (): Parameter[] => {
        var parameters: Parameter[] = [];

        parameters.push(new Parameter("description", projectName));
        parameters.push(new Parameter("codClient", codClient));
        parameters.push(new Parameter("clientName", clientName));

        return parameters;
    };

    const onEnter = () => {
        fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
    };
    const onButtonClick = async () => {
        setCurrentPage(1);
        setGoToPage(1);
        fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
    };
    const onRemoveFiltersClick = async () => {
        setCurrentPage(1);
        setGoToPage(1);
        setProjectName("");
        setClientName("");
        setCodClient("");
        setReloadSearch(true);
    };

    useEffect(() => {
        if (reloadSearch === true) {
            fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
            setReloadSearch(false);
        }
        return () => { };
    }, [reloadSearch]);



    const onNewTaskClick = () => {
        navigate(Routes.projects.create);
    };



    return (
        <>
            <Layout>
                <div className="pageContainer">
                    <div className="pageHeader">
                        <span className="pageTitle">
                            <FormattedMessage id="Screens.Projects.Title" />
                        </span>

                        <Row>
                            <Col
                                xl={2}
                                lg={3}
                                md={3}
                                sm={12}
                                xs={12}
                                style={{ marginTop: "1em", marginBottom: "1em" }}
                            >
                                <ButtonComponent
                                    text={intl.formatMessage({ id: "Screens.Project.Button.CreateProject" })}
                                    onClick={onNewTaskClick}
                                    disabled={false} />
                            </Col>
                        </Row>
                    </div>

                    <div className="pageBody">
                        <Row>
                            <InputComponent
                                xl={2}
                                lg={3}
                                md={5}
                                sm={12}
                                xs={12}
                                style={{ width: "100%" }}
                                type={"text"}
                                onEnter={onEnter}
                                value={projectName}
                                label={intl.formatMessage({ id: "Screens.Projects.SearchField1" })}
                                readOnly={false}
                                onChange={(t: string) => setProjectName(t)}
                            />                            
                            <InputComponent
                                xl={2}
                                lg={3}
                                md={5}
                                sm={12}
                                xs={12}
                                style={{ width: "100%" }}
                                type={"text"}
                                onEnter={onEnter}
                                value={clientName}
                                label={intl.formatMessage({ id: "Screens.Projects.SearchField3" })}
                                readOnly={false}
                                onChange={(t: string) => setClientName(t)}
                            />
                            <InputComponent
                                xl={2}
                                lg={3}
                                md={5}
                                sm={12}
                                xs={12}
                                style={{ width: "100%" }}
                                type={"text"}
                                onEnter={onEnter}
                                value={codClient}
                                label={intl.formatMessage({ id: "Screens.Projects.SearchField2" })}
                                readOnly={false}
                                onChange={(t: string) => setCodClient(t)}
                            />
                        </Row>

                        <Row>
                            <Col
                                xl={6}
                                lg={6}
                                md={6}
                                sm={12}
                                xs={12}
                                style={{ marginTop: "1em", display: "inline-flex", gap: "1rem" }}
                            >
                                <ButtonComponent
                                    text={intl.formatMessage({ id: "Common.Buttons.Search" })}
                                    onClick={onButtonClick}
                                    style={{minWidth:"100px"}}
                                />
                                <ButtonComponent
                                    text={intl.formatMessage({ id: "Common.Buttons.RemoveFilters" })}
                                    onClick={onRemoveFiltersClick}
                                    style={{minWidth:"113px"}}
                                />
                            </Col>
                        </Row>

                        <div className="pageComponent" style={{ width: "100%" }}>
                            <TableComponent
                                goToPage={goToPage}
                                loading={loading}
                                pageCount={pageCount}
                                columns={columns}
                                manualPagination={true}
                                fetchData={fetchData}
                                pageSizes={pageSizes}
                                initialPage={page}
                                initialPageSize={pageSize}
                                initialSortBy={initialSortBy}
                                data={data}
                                dontOrderFirstCollumn={true}
                            />
                        </div>
                    </div>
                </div>
            </Layout>
        </>
    );
}
